/**
 * Copyright 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button, Input, Label, Spacer, Text, theme } from "@nordcloud/gnui";
import { FormGroup, stringRequired } from "~/components/Forms";
import { useCustomer } from "~/context";
import { AWS_INFO_EXTERNAL_ID } from "~/views/accounts/AccountCreate/components/AccountDetailsForms/constants";
import { useUpdateCloudAccount } from "~/views/accounts/hooks";
import { CloudAccountFields } from "~/views/accounts/hooks/useUpdateCloudAccount/types";

export const schema = z.object({
  externalId: stringRequired("External ID"),
  arn: stringRequired("Role ARN"),
});

type Props = {
  onSuccess: () => void;
};

export function AwsForm({ onSuccess }: Props) {
  const customerContext = useCustomer();

  const { handleSubmit, register, formState, reset } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      externalId: customerContext.customer.id,
      arn: "",
    },
  });

  const [updateCloudAccount, loading] = useUpdateCloudAccount({
    onSuccess: () => {
      onSuccess();
      reset();
    },
  });

  const onSubmit = (data: CloudAccountFields) => updateCloudAccount(data);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormGroup error={formState.errors["externalId"]}>
        <Label name="Provide Role external ID" htmlFor="externalId" />
        <Text size="sm" color={theme.color.text.text02}>
          {AWS_INFO_EXTERNAL_ID}
        </Text>
        <Input
          id="externalId"
          disabled
          placeholder="Provide External ID"
          {...register("externalId")}
        />
      </FormGroup>
      <FormGroup error={formState.errors["arn"]}>
        <Label name="Provide Role ARN" htmlFor="arn" required />
        <Input
          id="arn"
          placeholder="e.g. arn:aws:iam::123456789012:role/NordcloudFleetControlServiceRole"
          {...register("arn")}
        />
      </FormGroup>
      <Spacer height={theme.spacing.spacing02} />
      <Button initialState={loading ? "loading" : "success"}>Apply</Button>
    </form>
  );
}
